import { FC, ReactElement, useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  Card,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";

import { formatNumberWithSuffix } from "@/utils";
import { tokens } from "@/tokens";

/* Data-viz accent colors (not in the design-token palette) */
const CHART_TEAL = "#0D9488";
const CHART_DUTY_BLUE = "#1E3A8A";

type ChartScaleFocus = "both" | "import" | "duty";

const CHART_FOCUS_EASE = "cubic-bezier(0.33, 1, 0.68, 1)";
const CHART_LAYER_TRANSITION = `opacity 0.5s ${CHART_FOCUS_EASE}`;
const CHART_STROKE_TRANSITION = `stroke-width 0.5s ${CHART_FOCUS_EASE}`;
const CHART_AREA_ANIMATION_MS = 520;

const strokeWidthImport = (focus: ChartScaleFocus) =>
  focus === "import" ? 4 : 2.25;
const strokeWidthDuty = (focus: ChartScaleFocus) =>
  focus === "duty" ? 4 : 2.5;

interface IMetric {
  title: string;
  value: string | number;
  icon: string;
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

interface ChartDatum {
  label: string;
  value: number;
  duty: number;
}

const formatAxisCurrency = (v: number): string => `$${formatNumberWithSuffix(v)}`;

const CombinedTooltip: FC<TooltipProps<number, string>> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload as ChartDatum | undefined;
  if (!row) return null;

  const importValue = row.value;
  const duties = row.duty;
  const effectiveRate = importValue > 0 ? (duties / importValue) * 100 : 0;

  return (
    <Box
      sx={{
        bgcolor: tokens.bg.surface,
        border: `1px solid ${tokens.border.default}`,
        borderRadius: "10px",
        boxShadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.06), 0 2px 4px -1px rgba(0, 0, 0, 0.04)",
        minWidth: 200,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          borderBottom: `1px solid ${tokens.border.default}`,
          bgcolor: tokens.bg.muted,
        }}
      >
        <Typography
          sx={{
            fontSize: tokens.fontSize.xs,
            fontWeight: tokens.fontWeight.semibold,
            color: tokens.text.body,
            letterSpacing: "0.02em",
          }}
        >
          {label}
        </Typography>
      </Box>

      <Stack sx={{ p: 1.25, gap: 0.75 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "3px",
                bgcolor: CHART_TEAL,
              }}
            />
            <Typography
              sx={{
                fontSize: tokens.fontSize.xs,
                color: tokens.text.secondary,
              }}
            >
              Import Value
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontSize: tokens.fontSize.base,
              fontWeight: tokens.fontWeight.bold,
              color: tokens.text.primary,
            }}
          >
            ${importValue.toLocaleString()}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "3px",
                bgcolor: CHART_DUTY_BLUE,
              }}
            />
            <Typography
              sx={{
                fontSize: tokens.fontSize.xs,
                color: tokens.text.secondary,
              }}
            >
              Duties Paid
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontSize: tokens.fontSize.base,
              fontWeight: tokens.fontWeight.bold,
              color: tokens.text.primary,
            }}
          >
            ${duties.toLocaleString()}
          </Typography>
        </Stack>

        <Box
          sx={{
            borderTop: `1px dashed ${tokens.border.default}`,
            pt: 0.75,
            mt: 0.25,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                fontSize: tokens.fontSize.xs,
                color: tokens.text.secondary,
              }}
            >
              Effective Rate
            </Typography>
            <Typography
              sx={{
                fontSize: tokens.fontSize.xs,
                fontWeight: tokens.fontWeight.semibold,
                color: tokens.text.body,
              }}
            >
              {effectiveRate.toFixed(2)}%
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

const ClickableSummaryStat: FC<{
  label: string;
  value: string;
  swatchColor: string;
  valueColor: string;
  selected: boolean;
  onClick: () => void;
  "aria-label": string;
}> = ({
  label,
  value,
  swatchColor,
  valueColor,
  selected,
  onClick,
  "aria-label": ariaLabel,
}) => (
  <ButtonBase
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    aria-label={ariaLabel}
    sx={{
      display: "block",
      textAlign: "right",
      borderRadius: tokens.radius.md,
      px: 1.25,
      py: 1,
      border: `1px solid ${
        selected ? swatchColor : tokens.border.default
      }`,
      bgcolor: selected ? alpha(swatchColor, 0.1) : tokens.bg.surface,
      transform: selected ? "scale(1.07)" : "scale(1)",
      transformOrigin: "center center",
      transition:
        "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
      boxShadow: selected
        ? `0 4px 12px -2px ${alpha(swatchColor, 0.35)}`
        : tokens.shadow.sm,
      "&:hover": {
        borderColor: swatchColor,
        bgcolor: alpha(swatchColor, 0.06),
      },
      "&.Mui-focusVisible": {
        outline: `2px solid ${tokens.brand.primary}`,
        outlineOffset: 2,
      },
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      justifyContent="flex-end"
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "2px",
          bgcolor: swatchColor,
        }}
      />
      <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
        {label}
      </Typography>
    </Stack>
    <Typography
      variant="h6"
      fontWeight={tokens.fontWeight.bold}
      sx={{
        color: valueColor,
        lineHeight: 1.25,
        fontSize: selected
          ? { xs: "1.05rem", sm: "1.15rem" }
          : undefined,
      }}
    >
      {value}
    </Typography>
  </ButtonBase>
);

const ImportCard: FC<{
  data?: ImportsValueReponse["dashboardVisibilityOverallImportValue"]["data"];
  xAxisMode: "month" | "quarter" | "year";
  isLoading: boolean;
  totalDuties?: number;
  metrics?: IMetric[];
}> = ({ data = [], xAxisMode, isLoading, metrics }): ReactElement => {
  const [chartScaleFocus, setChartScaleFocus] =
    useState<ChartScaleFocus>("both");

  const total = useMemo(
    () =>
      (data ?? [])
        .filter((item) => item.year != null)
        .reduce((prev, curr) => prev + curr.totalGoodsValue, 0),
    [data],
  );
  const totalDutiesSum = useMemo(
    () =>
      (data ?? [])
        .filter((item) => item.year != null)
        .reduce((prev, curr) => prev + (curr.totalDuties ?? 0), 0),
    [data],
  );

  const chartData = useMemo<ChartDatum[]>(() => {
    const safeData = (data ?? []).filter((item) => item.year != null);

    if (xAxisMode === "month") {
      return safeData.map(
        ({ year, month, totalGoodsValue, totalDuties: periodDuties }) => ({
          label: `${MONTHS[month - 1]} '${String(year).slice(-2)}`,
          value: totalGoodsValue,
          duty: periodDuties ?? 0,
        }),
      );
    }

    if (xAxisMode === "quarter") {
      const grouped: Record<string, { value: number; duty: number }> = {};
      safeData.forEach(
        ({ year, month, totalGoodsValue, totalDuties: periodDuties }) => {
          const q = Math.ceil(month / 3);
          const key = `Q${q} '${String(year).slice(-2)}`;
          if (!grouped[key]) grouped[key] = { value: 0, duty: 0 };
          grouped[key].value += totalGoodsValue;
          grouped[key].duty += periodDuties ?? 0;
        },
      );
      return Object.entries(grouped).map(([label, d]) => ({
        label,
        value: d.value,
        duty: d.duty,
      }));
    }

    const grouped: Record<string, { value: number; duty: number }> = {};
    safeData.forEach(
      ({ year, totalGoodsValue, totalDuties: periodDuties }) => {
        const key = String(year);
        if (!grouped[key]) grouped[key] = { value: 0, duty: 0 };
        grouped[key].value += totalGoodsValue;
        grouped[key].duty += periodDuties ?? 0;
      },
    );
    return Object.entries(grouped).map(([label, d]) => ({
      label,
      value: d.value,
      duty: d.duty,
    }));
  }, [data, xAxisMode]);

  const { maxImportInRange, maxDutyInRange } = useMemo(() => {
    if (!chartData.length) return { maxImportInRange: 0, maxDutyInRange: 0 };
    return {
      maxImportInRange: Math.max(...chartData.map((d) => d.value)),
      maxDutyInRange: Math.max(...chartData.map((d) => d.duty)),
    };
  }, [chartData]);

  const chartYMax = useMemo(() => {
    const pad = 1.06;
    switch (chartScaleFocus) {
      case "import":
        return maxImportInRange > 0 ? maxImportInRange * pad : 0;
      case "duty":
        return maxDutyInRange > 0 ? maxDutyInRange * pad : 0;
      default:
        return Math.max(maxImportInRange, maxDutyInRange) * pad;
    }
  }, [chartScaleFocus, maxImportInRange, maxDutyInRange]);

  const toggleImportFocus = () => {
    setChartScaleFocus((prev) => (prev === "import" ? "both" : "import"));
  };

  const toggleDutyFocus = () => {
    setChartScaleFocus((prev) => (prev === "duty" ? "both" : "duty"));
  };

  return (
    <Card
      sx={{
        p: { xs: 1.5, md: 2.5 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${tokens.border.default}`,
        boxShadow: "none",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "flex-start" }}
        sx={{ mb: 2, gap: { xs: 1.5, sm: 0 } }}
      >
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={tokens.fontWeight.bold}
            color={tokens.text.primary}
          >
            Imports & Duties Over Time
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
            Click a total to focus the chart on that series.
          </Typography>
        </Box>
        <Stack
          direction="row"
          alignItems="flex-start"
          spacing={{ xs: 1.5, md: 2 }}
        >
          <ClickableSummaryStat
            label="Import Value"
            value={`$${formatNumberWithSuffix(total)}`}
            swatchColor={CHART_TEAL}
            valueColor={CHART_TEAL}
            selected={chartScaleFocus === "import"}
            onClick={toggleImportFocus}
            aria-label="Import Value: click to show only import value on the chart, click again to show both series"
          />
          <ClickableSummaryStat
            label="Duties"
            value={`$${formatNumberWithSuffix(totalDutiesSum)}`}
            swatchColor={CHART_DUTY_BLUE}
            valueColor={CHART_DUTY_BLUE}
            selected={chartScaleFocus === "duty"}
            onClick={toggleDutyFocus}
            aria-label="Duties: click to show only duties on the chart, click again to show both series"
          />
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {isLoading ? (
          <Stack height="100%" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Stack>
        ) : chartData.length > 0 ? (
          <Box
            sx={{
              height: { xs: 160, sm: 180, md: 200, lg: 240 },
              width: "100%",
              "& g.analytics-area-import-layer": {
                opacity: chartScaleFocus === "duty" ? 0 : 1,
                transition: CHART_LAYER_TRANSITION,
                pointerEvents: chartScaleFocus === "duty" ? "none" : "auto",
              },
              "& g.analytics-area-duty-layer": {
                opacity: chartScaleFocus === "import" ? 0 : 1,
                transition: CHART_LAYER_TRANSITION,
                pointerEvents: chartScaleFocus === "import" ? "none" : "auto",
              },
              "& g.analytics-area-import-layer path": {
                transition: CHART_STROKE_TRANSITION,
              },
              "& g.analytics-area-duty-layer path": {
                transition: CHART_STROKE_TRANSITION,
              },
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 10, right: 8, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_TEAL} stopOpacity={0.32} />
                    <stop offset="95%" stopColor={CHART_TEAL} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="dutyGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_DUTY_BLUE} stopOpacity={0.26} />
                    <stop offset="95%" stopColor={CHART_DUTY_BLUE} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={tokens.border.default}
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: tokens.text.secondary }}
                  tickLine={false}
                  axisLine={{ stroke: tokens.border.default }}
                />
                <YAxis
                  allowDataOverflow
                  tick={{ fontSize: 11, fill: tokens.text.secondary }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={formatAxisCurrency}
                  domain={[0, chartYMax > 0 ? chartYMax : 1]}
                  width={54}
                />
                <Tooltip
                  cursor={{
                    stroke: tokens.border.default,
                    strokeDasharray: "3 3",
                  }}
                  content={<CombinedTooltip />}
                />
                <Area
                  className="analytics-area-import-layer"
                  type="monotone"
                  dataKey="value"
                  name="Import Value"
                  stroke={CHART_TEAL}
                  strokeWidth={strokeWidthImport(chartScaleFocus)}
                  fill="url(#valueGradient)"
                  isAnimationActive
                  animationDuration={CHART_AREA_ANIMATION_MS}
                  animationEasing="ease-out"
                  activeDot={
                    chartScaleFocus === "duty"
                      ? false
                      : {
                          r: chartScaleFocus === "import" ? 6 : 4,
                          strokeWidth: 2,
                          stroke: tokens.bg.surface,
                        }
                  }
                />
                <Area
                  className="analytics-area-duty-layer"
                  type="monotone"
                  dataKey="duty"
                  name="Duties"
                  stroke={CHART_DUTY_BLUE}
                  strokeWidth={strokeWidthDuty(chartScaleFocus)}
                  fill="url(#dutyGradient)"
                  isAnimationActive
                  animationDuration={CHART_AREA_ANIMATION_MS}
                  animationEasing="ease-out"
                  activeDot={
                    chartScaleFocus === "import"
                      ? false
                      : {
                          r: chartScaleFocus === "duty" ? 6 : 4,
                          strokeWidth: 2,
                          stroke: tokens.bg.surface,
                        }
                  }
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: "100%" }}
          >
            <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
              No data available
            </Typography>
          </Stack>
        )}
      </Box>

      {/* Metrics row */}
      {metrics && metrics.length > 0 && (
        <Stack
          direction="row"
          justifyContent="center"
          sx={{
            borderTop: `1px solid ${tokens.border.default}`,
            pt: 1.5,
            mt: 0.5,
            gap: { xs: 3, sm: 5, md: 7 },
          }}
        >
          {metrics.map(({ title, value, icon }, index) => (
            <Stack
              key={index}
              direction="row"
              alignItems="center"
              spacing={0.75}
            >
              <Box
                component="img"
                src={icon}
                alt=""
                sx={{ width: 16, height: 16, opacity: 0.35 }}
              />
              <Box>
                <Typography
                  sx={{
                    color: tokens.text.secondary,
                    fontSize: { xs: "8px", md: "9px", lg: "10px" },
                    lineHeight: 1.2,
                    opacity: 0.8,
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  fontWeight={tokens.fontWeight.bold}
                  sx={{
                    color: tokens.text.body,
                    fontSize: { xs: "10px", md: "11px", lg: "12px" },
                    lineHeight: 1.2,
                  }}
                >
                  {value}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </Card>
  );
};

export default ImportCard;
